// File: src/pages/ContactsPage.jsx
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, Button, Card, CardContent, Container, Grid, Typography, TextField, 
  InputAdornment, IconButton, Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, Paper, Stack, Divider, Chip, Tooltip, LinearProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import EditNoteIcon from '@mui/icons-material/EditNote';

import { AuthContext } from '../context/AuthContext';
import { createAuthenticatedAPI } from '../utils/apiClient';
import QRScannerModal from '../components/QRScannerModal';

// Lazy import tesseract when needed
let Tesseract; // assigned on demand

const extractFields = (text) => {
  const result = { name: '', company: '', email: '', phone: '', website: '', extracted_text: text, ocr_confidence: undefined };
  if (!text) return result;
  const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);

  // email
  const emailMatch = text.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
  if (emailMatch) result.email = emailMatch[0];

  // website
  const urlMatch = text.match(/https?:\/\/[\w.-]+(?:\.[\w\.-]+)+(?:[\w\-\._~:\/?#\[\]@!$&'\(\)\*\+,;=.]+)?/i) || text.match(/\b[\w.-]+\.[a-z]{2,}\b/i);
  if (urlMatch) result.website = urlMatch[0];

  // phone (simple heuristic)
  const phoneMatch = text.match(/(?:\+?\d[\s-]?){7,15}/);
  if (phoneMatch) result.phone = phoneMatch[0].replace(/\s+/g, ' ').trim();

  // name/company guess from top lines
  if (lines.length) {
    result.name = lines[0];
    if (lines.length > 1) result.company = lines[1];
  }
  return result;
};

// Simple canvas preprocessing: grayscale + contrast + threshold
const preprocessImage = (img) => {
  const canvas = document.createElement('canvas');
  const maxDim = 1500;
  let { width, height } = img;
  const scale = Math.min(1, maxDim / Math.max(width, height));
  width = Math.floor(width * scale);
  height = Math.floor(height * scale);
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, width, height);
  const imageData = ctx.getImageData(0, 0, width, height);
  const d = imageData.data;
  // Grayscale and histogram
  let sum = 0;
  for (let i = 0; i < d.length; i += 4) {
    const r = d[i], g = d[i+1], b = d[i+2];
    const gray = (r * 0.299 + g * 0.587 + b * 0.114);
    d[i] = d[i+1] = d[i+2] = gray;
    sum += gray;
  }
  const avg = sum / (d.length / 4);
  // Adaptive-ish threshold using global mean with bias
  const bias = 10; // tweakable
  for (let i = 0; i < d.length; i += 4) {
    const v = d[i] < (avg - bias) ? 0 : 255;
    d[i] = d[i+1] = d[i+2] = v;
  }
  ctx.putImageData(imageData, 0, 0);
  return canvas;
};

const ContactsPage = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const api = useMemo(() => createAuthenticatedAPI({ user, logout, navigate }), [user, logout, navigate]);

  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState({ name: '', company: '', email: '', phone: '', website: '', notes: '' });
  const [imageFile, setImageFile] = useState(null);
  const fileInputRef = useRef(null);
  const [scanOpen, setScanOpen] = useState(false);
  const [previewSrc, setPreviewSrc] = useState('');
  const [fieldConf, setFieldConf] = useState({});
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrProgress, setOcrProgress] = useState(0);

  useEffect(() => { loadContacts(); }, []);

  const loadContacts = async () => {
    const list = await api.get(`/contacts?q=${encodeURIComponent(search)}`);
    setContacts(list);
  };

  const handleCapture = async (file) => {
    if (!file) return;
    setImageFile(file);
    // Preview
    const url = URL.createObjectURL(file);
    setPreviewSrc(url);
    setOcrLoading(true);
    setOcrProgress(5);
    if (!Tesseract) {
      Tesseract = (await import('tesseract.js')).default;
    }
    // Load image for preprocessing
    const img = new Image();
    await new Promise((res) => { img.onload = res; img.src = url; });
    const processedCanvas = preprocessImage(img);
    setOcrProgress(15);
    const { data } = await Tesseract.recognize(processedCanvas, 'eng', { tessedit_pageseg_mode: '6', logger: (m) => {
      if (m && typeof m.progress === 'number') {
        setOcrProgress(Math.max(20, Math.min(99, Math.round(m.progress * 100))));
      }
    } });
    const fields = extractFields(data.text || '');
    // Confidence estimation per field using words data
    const words = Array.isArray(data.words) ? data.words : [];
    const conf = {};
    const avgConf = words.length ? (words.reduce((s, w) => s + (w.confidence || 0), 0) / words.length) / 100 : 0;
    const confFor = (pattern) => {
      const re = (pattern instanceof RegExp) ? pattern : new RegExp(pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i');
      const matched = words.filter(w => re.test(w.text || ''));
      if (!matched.length) return 0;
      return (matched.reduce((s, w) => s + (w.confidence || 0), 0) / matched.length) / 100;
    };
    conf.email = fields.email ? confFor(fields.email) || Math.max(0.6, avgConf) : 0;
    conf.phone = fields.phone ? Math.max(0.5, avgConf * 0.9) : 0;
    conf.website = fields.website ? Math.max(0.5, avgConf * 0.9) : 0;
    conf.name = fields.name ? Math.max(0.4, avgConf * 0.8) : 0;
    conf.company = fields.company ? Math.max(0.4, avgConf * 0.8) : 0;
    conf.overall = avgConf;
    setFieldConf(conf);
    setForm(prev => ({ ...prev, ...fields }));
    setOcrProgress(100);
    setTimeout(() => setOcrLoading(false), 200);
  };

  const saveContact = async () => {
    const payload = {
      ...form,
      source: 'physical',
      // For simplicity we skip upload and store no URL; can be added later
      extracted_text: form.extracted_text,
    };
    const res = await api.post('/contacts', payload);
    const saved = res.contact;
    const match = res.match;
    setForm({ name: '', company: '', email: '', phone: '', website: '', notes: '' });
    setImageFile(null);
    await loadContacts();
    // Navigate to matched public profile if available
    if (match?.public_slug) {
      navigate(`/u/${match.public_slug}`);
      return;
    }
    // Else, show invite CTA
    alert('Contact saved. Invite them to Touch Connect from this contact.');
  };

  const handleDecoded = async (text) => {
    // If it looks like a TouchConnect profile URL (/u/{slug}), navigate
    try {
      const url = new URL(text);
      const match = url.pathname.match(/^\/?u\/(.+)$/);
      if (match && match[1]) {
        navigate(`/u/${match[1]}`);
        return;
      }
    } catch {}
    // Otherwise, save as a QR-sourced contact with website set to the value
    try {
      const payload = {
        name: '', company: '', email: '', phone: '',
        website: text, notes: '', source: 'qr'
      };
      await api.post('/contacts', payload);
      await loadContacts();
      alert('QR captured and saved as a contact (source: qr).');
    } catch (e) {
      alert('Failed to save from QR.');
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(180deg, #f8fafc, #eef2f7)' }}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 800, mb: 3 }}>Contacts</Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={5}>
            <Card sx={{ borderRadius: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Add Contact</Typography>
                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                  <Button variant="outlined" startIcon={<QrCodeScannerIcon />} onClick={() => setScanOpen(true)}>
                    Scan QR
                  </Button>
                  <Button variant="outlined" startIcon={<AddAPhotoIcon />} onClick={() => fileInputRef.current?.click()}>
                    Scan Card Photo
                  </Button>
                  <input ref={fileInputRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => handleCapture(e.target.files?.[0])} />
                  <Button variant="outlined" startIcon={<EditNoteIcon />} onClick={() => { /* manual only */ }}>
                    Manual
                  </Button>
                </Stack>
                {/* Guided capture tips and preview */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Tip: place the card flat in good light, fill the frame, and avoid shadows.
                  </Typography>
                  {ocrLoading && (
                    <Box sx={{ mt: 1 }}>
                      <LinearProgress variant={ocrProgress > 0 ? 'determinate' : 'indeterminate'} value={ocrProgress} />
                      <Typography variant="caption" color="text.secondary">Processing image… {ocrProgress}%</Typography>
                    </Box>
                  )}
                  {previewSrc && (
                    <Box sx={{ mt: 1, position: 'relative', borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                      <img src={previewSrc} alt="preview" style={{ width: '100%', display: 'block' }} />
                      {/* Corner guides */}
                      <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
                        <Box sx={{ position: 'absolute', top: 8, left: 8, width: 28, height: 2, bgcolor: 'primary.main' }} />
                        <Box sx={{ position: 'absolute', top: 8, left: 8, width: 2, height: 28, bgcolor: 'primary.main' }} />
                        <Box sx={{ position: 'absolute', top: 8, right: 8, width: 28, height: 2, bgcolor: 'primary.main' }} />
                        <Box sx={{ position: 'absolute', top: 8, right: 8, width: 2, height: 28, bgcolor: 'primary.main' }} />
                        <Box sx={{ position: 'absolute', bottom: 8, left: 8, width: 28, height: 2, bgcolor: 'primary.main' }} />
                        <Box sx={{ position: 'absolute', bottom: 8, left: 8, width: 2, height: 28, bgcolor: 'primary.main' }} />
                        <Box sx={{ position: 'absolute', bottom: 8, right: 8, width: 28, height: 2, bgcolor: 'primary.main' }} />
                        <Box sx={{ position: 'absolute', bottom: 8, right: 8, width: 2, height: 28, bgcolor: 'primary.main' }} />
                      </Box>
                    </Box>
                  )}
                </Box>

                <Grid container spacing={2}>
                  {['name','company','email','phone','website','notes'].map((field) => (
                    <Grid item xs={12} key={field}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TextField
                          fullWidth
                          label={field.charAt(0).toUpperCase() + field.slice(1)}
                          value={form[field] || ''}
                          onChange={(e) => setForm(prev => ({ ...prev, [field]: e.target.value }))}
                        />
                        {field !== 'notes' && (fieldConf?.[field] != null) && (
                          <Tooltip title={`Confidence: ${Math.round((fieldConf[field] || 0)*100)}%`}>
                            <Chip size="small" label={`${Math.round((fieldConf[field] || 0)*100)}%`} color={(fieldConf[field] || 0) >= 0.8 ? 'success' : (fieldConf[field] || 0) >= 0.6 ? 'info' : (fieldConf[field] || 0) >= 0.4 ? 'warning' : 'default'} />
                          </Tooltip>
                        )}
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                  <Button variant="contained" onClick={saveContact}>Save Contact</Button>
                  <Button variant="text" onClick={() => { setForm({ name: '', company: '', email: '', phone: '', website: '', notes: '' }); setImageFile(null); }}>Clear</Button>
                </Stack>
              </CardContent>
            </Card>
            <QRScannerModal open={scanOpen} onClose={() => setScanOpen(false)} onDecoded={handleDecoded} />
          </Grid>

          <Grid item xs={12} md={7}>
            <Card sx={{ borderRadius: 3 }}>
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                  <TextField
                    placeholder="Search contacts"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{ startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    )}}
                    sx={{ minWidth: 280 }}
                  />
                  <Button variant="outlined" onClick={loadContacts}>Search</Button>
                </Stack>

                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Company</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Phone</TableCell>
                        <TableCell>Website</TableCell>
                        <TableCell>Source</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {contacts.map((c) => (
                        <TableRow key={c.id} hover>
                          <TableCell>{c.name}</TableCell>
                          <TableCell>{c.company}</TableCell>
                          <TableCell>{c.email}</TableCell>
                          <TableCell>{c.phone}</TableCell>
                          <TableCell>{c.website}</TableCell>
                          <TableCell>{c.source}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default ContactsPage;


